import { useState, useCallback, useRef, useEffect } from 'react';
import { useVeloraStore } from '../store/useVeloraStore';
import { AudioCapture } from '../lib/audioCapture';
import { classifyVoiceType } from '../lib/vocalRange';
import { frequencyToNote } from '../lib/noteMap';

const SINGING_STYLES = [
  { id: 'pop',       label: 'Pop',        emoji: '🎤' },
  { id: 'rock',      label: 'Rock',       emoji: '🎸' },
  { id: 'classical', label: 'Classical',  emoji: '🎻' },
  { id: 'jazz',      label: 'Jazz',       emoji: '🎷' },
  { id: 'rnb',       label: 'R&B / Soul', emoji: '🎶' },
  { id: 'musical',   label: 'Musical',    emoji: '🎭' },
  { id: 'folk',      label: 'Folk',       emoji: '🪕' },
  { id: 'any',       label: 'Everything', emoji: '✨' },
];

const TRAINING_GOALS = [
  { id: 'learn_songs',    label: 'Learn Songs',        sub: 'Practice specific songs & lyrics',    emoji: '🎵' },
  { id: 'technique',      label: 'Improve Technique',  sub: 'Pitch accuracy, breath control',       emoji: '🎯' },
  { id: 'daily_warmup',   label: 'Daily Warmup',       sub: 'Quick 10-min voice prep routine',      emoji: '☀️' },
  { id: 'performance',    label: 'Performance Prep',   sub: 'Build stamina & stage confidence',     emoji: '🌟' },
];

type Step = 'welcome' | 'name' | 'range' | 'range_test' | 'styles' | 'goal' | 'done';

const PHASE_DURATION = 10000;
const MIN_F = 60, MAX_F = 1400;

export default function Onboarding() {
  const { setOnboarded, setUserName, setSingingStyles, setTrainingGoal, setVocalRange } = useVeloraStore();

  const [step, setStep] = useState<Step>('welcome');
  const [name, setName] = useState('');
  const [styles, setStyles] = useState<string[]>([]);
  const [goal, setGoal] = useState('');
  const [phase, setPhase] = useState<'low' | 'high' | 'done' | null>(null);
  const [lowestHz, setLowestHz] = useState(Infinity);
  const [highestHz, setHighestHz] = useState(0);
  const [currentHz, setCurrentHz] = useState(0);
  const [timeLeft, setTimeLeft] = useState(PHASE_DURATION / 1000);
  const capture = useRef<AudioCapture | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdown = useRef<ReturnType<typeof setInterval> | null>(null);

  const cleanup = useCallback(() => {
    capture.current?.stop(); capture.current = null;
    if (timer.current)    { clearInterval(timer.current);    timer.current = null; }
    if (countdown.current){ clearInterval(countdown.current); countdown.current = null; }
  }, []);
  useEffect(() => () => cleanup(), [cleanup]);

  const startPhase = useCallback(async (p: 'low' | 'high') => {
    cleanup();
    setPhase(p); setTimeLeft(PHASE_DURATION / 1000);
    if (p === 'low') { setLowestHz(Infinity); setHighestHz(0); }
    capture.current = new AudioCapture(({ frequency, rms }) => {
      if (rms < 0.02 || frequency <= 0 || frequency < MIN_F || frequency > MAX_F) return;
      setCurrentHz(frequency);
      if (p === 'low')  setLowestHz(prev => Math.min(prev, frequency));
      if (p === 'high') setHighestHz(prev => Math.max(prev, frequency));
    });
    await capture.current.start();
    countdown.current = setInterval(() => setTimeLeft(t => Math.max(0, t - 1)), 1000);
    timer.current = setTimeout(() => {
      cleanup();
      if (p === 'low') startPhase('high');
      else { setPhase('done'); setCurrentHz(0); }
    }, PHASE_DURATION);
  }, [cleanup]);

  function saveRange() {
    if (lowestHz !== Infinity && highestHz > 0) {
      const type = classifyVoiceType(lowestHz, highestHz);
      setVocalRange(lowestHz, highestHz, frequencyToNote(lowestHz).fullName, frequencyToNote(highestHz).fullName, type);
    }
  }

  function finish() {
    saveRange();
    setUserName(name.trim() || 'Singer');
    setSingingStyles(styles.length ? styles : ['any']);
    setTrainingGoal(goal || 'technique');
    setOnboarded(true);
  }

  const toggleStyle = (id: string) => {
    if (id === 'any') { setStyles(['any']); return; }
    setStyles(prev => {
      const without = prev.filter(s => s !== 'any');
      return without.includes(id) ? without.filter(s => s !== id) : [...without, id];
    });
  };

  // ── STEP COMPONENTS ──────────────────────────────────────────────────

  if (step === 'welcome') return (
    <div style={PAGE}>
      <Orbs />
      <div style={CONTENT}>
        <div style={{ fontSize: 72, marginBottom: 24, filter: 'drop-shadow(0 0 24px rgba(127,119,221,0.5))' }}>🎤</div>
        <h1 style={H1}>Welcome to<br /><span style={{ color: '#9B94E8' }}>Velora</span></h1>
        <p style={SUB}>Your AI vocal training companion.<br />Let's set you up in 2 minutes.</p>
        <div style={{ marginTop: 40, width: '100%', maxWidth: 320, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <button style={BTN_PRIMARY} onClick={() => setStep('name')}>Get Started →</button>
          <button style={BTN_GHOST} onClick={finish}>Skip setup</button>
        </div>
      </div>
    </div>
  );

  if (step === 'name') return (
    <div style={PAGE}>
      <Orbs />
      <div style={CONTENT}>
        <StepDots total={4} current={0} />
        <div style={{ fontSize: 48, marginBottom: 20 }}>👋</div>
        <h2 style={H2}>What's your name?</h2>
        <p style={SUB}>We'll personalise your experience.</p>
        <input
          autoFocus
          placeholder="Your name"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && setStep('range')}
          style={INPUT}
        />
        <div style={ROW_BTNS}>
          <button style={BTN_PRIMARY} onClick={() => setStep('range')} disabled={!name.trim()}>Continue →</button>
          <button style={BTN_GHOST} onClick={() => setStep('range')}>Skip</button>
        </div>
      </div>
    </div>
  );

  if (step === 'range') return (
    <div style={PAGE}>
      <Orbs />
      <div style={CONTENT}>
        <StepDots total={4} current={1} />
        <div style={{ fontSize: 48, marginBottom: 20 }}>🎭</div>
        <h2 style={H2}>Discover your<br />voice type</h2>
        <p style={SUB}>We'll test your vocal range.<br />Takes about 24 seconds — totally optional.</p>
        <div style={{ width: '100%', maxWidth: 320, display: 'flex', flexDirection: 'column', gap: 12, marginTop: 32 }}>
          <button style={BTN_ACCENT} onClick={() => { setStep('range_test'); startPhase('low'); }}>
            🎤 Start Range Test
          </button>
          <button style={BTN_GHOST} onClick={() => setStep('styles')}>Skip for now</button>
        </div>
      </div>
    </div>
  );

  if (step === 'range_test') return (
    <div style={PAGE}>
      <Orbs />
      <div style={{ ...CONTENT, gap: 16 }}>
        <StepDots total={4} current={1} />

        {phase !== 'done' && (
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: 'rgba(238,237,254,0.5)', fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8, fontFamily: 'Syne, sans-serif' }}>
              {phase === 'low' ? 'Step 1 of 2' : 'Step 2 of 2'}
            </p>
            <h2 style={H2}>{phase === 'low' ? 'Sing your LOWEST note' : 'Sing your HIGHEST note'}</h2>
            <p style={{ ...SUB, marginBottom: 20 }}>{phase === 'low' ? 'Go as low as you comfortably can' : 'Push as high as feels comfortable'}</p>

            {/* Timer ring */}
            <svg width="100" height="100" viewBox="0 0 36 36" style={{ display: 'block', margin: '0 auto 16px' }}>
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="2.8"/>
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#9B94E8" strokeWidth="2.8"
                strokeDasharray={`${(timeLeft / (PHASE_DURATION/1000)) * 100} 100`}
                strokeDashoffset="25" strokeLinecap="round"
                style={{ transition: 'stroke-dasharray 1s linear', filter: 'drop-shadow(0 0 4px rgba(155,148,232,0.7))' }}/>
              <text x="18" y="22" textAnchor="middle" fontSize="10" fill="#EEEDFE" fontFamily="JetBrains Mono, monospace">{timeLeft}s</text>
            </svg>

            <div style={{ fontSize: 44, fontFamily: 'Syne, sans-serif', fontWeight: 700, color: currentHz > 0 ? '#EEEDFE' : 'rgba(238,237,254,0.25)', textShadow: currentHz > 0 ? '0 0 20px rgba(155,148,232,0.6)' : 'none', marginBottom: 4 }}>
              {currentHz > 0 ? frequencyToNote(currentHz).fullName : '•'}
            </div>
            {currentHz > 0 && <div style={{ color: 'rgba(238,237,254,0.4)', fontSize: 12, fontFamily: 'JetBrains Mono, monospace' }}>{currentHz.toFixed(1)} Hz</div>}

            <button style={{ ...BTN_GHOST, marginTop: 20 }} onClick={() => { cleanup(); if (phase === 'low') startPhase('high'); else { setPhase('done'); } }}>
              Skip to {phase === 'low' ? 'High' : 'Results'}
            </button>
          </div>
        )}

        {phase === 'done' && (
          <div style={{ textAlign: 'center', width: '100%' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <h2 style={H2}>Range captured!</h2>
            {lowestHz !== Infinity && highestHz > 0 && (
              <div style={{ ...GLASS_CARD, maxWidth: 280, margin: '16px auto', padding: 16 }}>
                <p style={{ color: 'rgba(238,237,254,0.5)', fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8, fontFamily: 'Syne, sans-serif' }}>Your Voice Type</p>
                <p style={{ color: '#9B94E8', fontSize: 28, fontFamily: 'Syne, sans-serif', fontWeight: 700 }}>
                  {classifyVoiceType(lowestHz, highestHz)}
                </p>
                <p style={{ color: 'rgba(238,237,254,0.5)', fontSize: 13, marginTop: 6, fontFamily: 'JetBrains Mono, monospace' }}>
                  {frequencyToNote(lowestHz).fullName} – {frequencyToNote(highestHz).fullName}
                </p>
              </div>
            )}
            <button style={{ ...BTN_PRIMARY, maxWidth: 280, marginTop: 16 }} onClick={() => setStep('styles')}>Continue →</button>
          </div>
        )}
      </div>
    </div>
  );

  if (step === 'styles') return (
    <div style={PAGE}>
      <Orbs />
      <div style={CONTENT}>
        <StepDots total={4} current={2} />
        <div style={{ fontSize: 40, marginBottom: 16 }}>🎼</div>
        <h2 style={H2}>What do you love to sing?</h2>
        <p style={SUB}>Pick one or more — we'll tailor your experience.</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, width: '100%', maxWidth: 340, marginTop: 20, marginBottom: 24 }}>
          {SINGING_STYLES.map(s => {
            const active = styles.includes(s.id);
            return (
              <button key={s.id} onClick={() => toggleStyle(s.id)} style={{
                ...GLASS_CARD,
                padding: '14px 10px',
                border: `1px solid ${active ? 'rgba(127,119,221,0.50)' : 'rgba(255,255,255,0.10)'}`,
                background: active ? 'rgba(83,74,183,0.22)' : 'rgba(255,255,255,0.055)',
                boxShadow: active ? '0 4px 16px rgba(83,74,183,0.25)' : undefined,
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                transition: 'all 0.15s',
              }}>
                <span style={{ fontSize: 24 }}>{s.emoji}</span>
                <span style={{ color: active ? '#EEEDFE' : 'rgba(238,237,254,0.55)', fontSize: 13, fontFamily: 'Syne, sans-serif', fontWeight: 600 }}>{s.label}</span>
              </button>
            );
          })}
        </div>
        <button style={{ ...BTN_PRIMARY, maxWidth: 320 }} onClick={() => setStep('goal')} disabled={styles.length === 0}>Continue →</button>
        <button style={BTN_GHOST} onClick={() => setStep('goal')}>Skip</button>
      </div>
    </div>
  );

  if (step === 'goal') return (
    <div style={PAGE}>
      <Orbs />
      <div style={CONTENT}>
        <StepDots total={4} current={3} />
        <div style={{ fontSize: 40, marginBottom: 16 }}>🎯</div>
        <h2 style={H2}>What's your main goal?</h2>
        <p style={SUB}>This helps us recommend exercises and songs.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 340, marginTop: 20, marginBottom: 24 }}>
          {TRAINING_GOALS.map(g => {
            const active = goal === g.id;
            return (
              <button key={g.id} onClick={() => setGoal(g.id)} style={{
                ...GLASS_CARD,
                padding: '14px 16px',
                border: `1px solid ${active ? 'rgba(127,119,221,0.50)' : 'rgba(255,255,255,0.10)'}`,
                background: active ? 'rgba(83,74,183,0.22)' : 'rgba(255,255,255,0.055)',
                boxShadow: active ? '0 4px 16px rgba(83,74,183,0.25)' : undefined,
                display: 'flex', alignItems: 'center', gap: 14,
                transition: 'all 0.15s', textAlign: 'left',
              }}>
                <span style={{ fontSize: 28, flexShrink: 0 }}>{g.emoji}</span>
                <div>
                  <p style={{ color: active ? '#EEEDFE' : 'rgba(238,237,254,0.80)', fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 14, margin: 0 }}>{g.label}</p>
                  <p style={{ color: 'rgba(238,237,254,0.45)', fontSize: 12, margin: '2px 0 0', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{g.sub}</p>
                </div>
                {active && <span style={{ marginLeft: 'auto', color: '#9B94E8', fontSize: 18 }}>✓</span>}
              </button>
            );
          })}
        </div>
        <button style={{ ...BTN_PRIMARY, maxWidth: 320 }} onClick={finish} disabled={!goal}>Start Training →</button>
        <button style={BTN_GHOST} onClick={finish}>Skip</button>
      </div>
    </div>
  );

  return null;
}

// ── HELPERS ──────────────────────────────────────────────────────────────────

function Orbs() {
  return (
    <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
      <div style={{ position: 'absolute', width: 380, height: 380, borderRadius: '50%', background: 'radial-gradient(circle, rgba(83,74,183,0.55) 0%, transparent 70%)', top: -80, left: -60, filter: 'blur(72px)' }} />
      <div style={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(127,119,221,0.35) 0%, transparent 70%)', bottom: '15%', right: -40, filter: 'blur(64px)' }} />
      <div style={{ position: 'absolute', width: 260, height: 260, borderRadius: '50%', background: 'radial-gradient(circle, rgba(168,85,247,0.22) 0%, transparent 70%)', bottom: -60, left: '20%', filter: 'blur(80px)' }} />
    </div>
  );
}

function StepDots({ total, current }: { total: number; current: number }) {
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 24 }}>
      {Array.from({ length: total }, (_, i) => (
        <div key={i} style={{ height: 4, borderRadius: 2, transition: 'all 0.3s', width: i === current ? 24 : 8, background: i === current ? '#9B94E8' : 'rgba(255,255,255,0.18)' }} />
      ))}
    </div>
  );
}

// ── STYLES ────────────────────────────────────────────────────────────────────
const PAGE: React.CSSProperties = {
  position: 'fixed', inset: 0,
  background: '#050210',
  display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center',
  padding: '32px 20px',
  overflowY: 'auto',
};
const CONTENT: React.CSSProperties = {
  position: 'relative', zIndex: 1,
  display: 'flex', flexDirection: 'column',
  alignItems: 'center', width: '100%', maxWidth: 400,
  gap: 8, textAlign: 'center',
};
const H1: React.CSSProperties = {
  fontFamily: 'Syne, sans-serif', fontSize: 36, fontWeight: 800,
  color: '#EEEDFE', lineHeight: 1.15, margin: 0,
};
const H2: React.CSSProperties = {
  fontFamily: 'Syne, sans-serif', fontSize: 26, fontWeight: 700,
  color: '#EEEDFE', lineHeight: 1.2, margin: 0,
};
const SUB: React.CSSProperties = {
  color: 'rgba(238,237,254,0.50)', fontSize: 14, lineHeight: 1.6,
  fontFamily: 'Plus Jakarta Sans, sans-serif', margin: 0,
};
const INPUT: React.CSSProperties = {
  width: '100%', maxWidth: 320,
  background: 'rgba(255,255,255,0.07)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 14, padding: '14px 16px',
  color: '#EEEDFE', fontSize: 18,
  fontFamily: 'Plus Jakarta Sans, sans-serif',
  outline: 'none', marginTop: 16,
};
const BTN_PRIMARY: React.CSSProperties = {
  width: '100%', padding: '15px', borderRadius: 16,
  background: 'rgba(83,74,183,0.28)',
  backdropFilter: 'blur(24px)',
  border: '1px solid rgba(127,119,221,0.45)',
  color: '#EEEDFE', fontSize: 16, fontWeight: 700,
  fontFamily: 'Syne, sans-serif', letterSpacing: 1,
  cursor: 'pointer',
  boxShadow: '0 8px 28px rgba(83,74,183,0.30), inset 0 1px 0 rgba(255,255,255,0.12)',
  transition: 'transform 0.12s', marginTop: 8,
};
const BTN_ACCENT: React.CSSProperties = {
  ...BTN_PRIMARY,
  background: 'rgba(155,148,232,0.20)',
  border: '1px solid rgba(155,148,232,0.45)',
};
const BTN_GHOST: React.CSSProperties = {
  background: 'rgba(255,255,255,0.04)',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 12, color: 'rgba(238,237,254,0.38)',
  fontSize: 13, cursor: 'pointer',
  padding: '10px 24px', fontFamily: 'Plus Jakarta Sans, sans-serif',
};
const ROW_BTNS: React.CSSProperties = {
  display: 'flex', flexDirection: 'column',
  gap: 10, width: '100%', maxWidth: 320, marginTop: 8,
};
const GLASS_CARD: React.CSSProperties = {
  background: 'rgba(255,255,255,0.055)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  borderRadius: 16, border: '1px solid rgba(255,255,255,0.10)',
  boxShadow: '0 4px 20px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.10)',
  cursor: 'pointer', width: '100%',
};
